import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { SearchBar } from '@/components/search/SearchBar';
import { RecentSearches } from '@/components/search/RecentSearches';
import { PopularSearches } from '@/components/search/PopularSearches';
import { SearchResults } from '@/components/search/SearchResults';
import { useSearchStore } from '@/store/search';

// 임시 인기 검색어 데이터
const POPULAR_SEARCHES = [
  { id: '1', query: '치킨', rank: 1 },
  { id: '2', query: '피자', rank: 2 },
  { id: '3', query: '햄버거', rank: 3 },
  { id: '4', query: '김밥', rank: 4 },
  { id: '5', query: '샐러드', rank: 5 },
  { id: '6', query: '도시락', rank: 6 },
  { id: '7', query: '파스타', rank: 7 },
  { id: '8', query: '초밥', rank: 8 },
  { id: '9', query: '강남역', rank: 9 },
  { id: '10', query: '종로', rank: 10 }
];

export function DesktopSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { 
    searchQuery, 
    setSearchQuery, 
    recentSearches, 
    addRecentSearch, 
    removeRecentSearch, 
    clearRecentSearches 
  } = useSearchStore();

  // URL의 쿼리 파라미터와 상태 동기화
  useEffect(() => {
    const query = searchParams.get('q') || '';
    setSearchQuery(query);
  }, [searchParams, setSearchQuery]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // URL 업데이트 (히스토리 추가하지 않음)
    if (value) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());
    }
  };

  const handleRecentSearchClick = (query: string) => {
    setSearchQuery(query);
    setSearchParams({ q: query });
  };

  const handlePopularSearchClick = (query: string) => {
    setSearchQuery(query);
    setSearchParams({ q: query });
    addRecentSearch(query);
  };

  const handleStoreClick = (storeId: string) => {
    // 검색 쿼리를 저장하고 매장 상세 페이지로 이동
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());
    }
    navigate(`/store/${storeId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <DesktopLayout>
      <div className="max-w-4xl mx-auto py-6">
        {/* Search Bar */}
        <div className="bg-white rounded-lg p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1">
              <SearchBar
                value={searchQuery}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
                placeholder="매장명, 메뉴, 지역으로 검색"
                autoFocus
              />
            </div>
          </div>
        </div>

        {/* Search Results or Suggestions */}
        {searchQuery ? (
          <SearchResults
            query={searchQuery}
            onResultClick={handleStoreClick}
          />
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {/* Recent Searches */}
            <div className="bg-white rounded-lg p-6">
              <RecentSearches
                searches={recentSearches}
                onSearchClick={handleRecentSearchClick}
                onSearchRemove={removeRecentSearch}
                onClearAll={clearRecentSearches}
              />
            </div>

            {/* Popular Searches */}
            <div className="bg-white rounded-lg p-6">
              <PopularSearches
                searches={POPULAR_SEARCHES}
                onSearchClick={handlePopularSearchClick}
              />
            </div>
          </div>
        )}
      </div>
    </DesktopLayout>
  );
}